import prepareInsights from '../assets/case-studies/kit/prepare-insights.webp';
import captureSideline from '../assets/case-studies/kit/capture-sideline.webp';
import decideShortlist from '../assets/case-studies/kit/decide-shortlist.webp';
import feedback from '../assets/case-studies/kit/feedback.webp';
import uiCompare from '../assets/case-studies/kit/ui-compare.webp';
import directions from '../assets/case-studies/kit/evolve/visual-directions.webp';
import finalRoster from '../assets/case-studies/kit/evolve/final-roster.webp';
import finalMoments from '../assets/case-studies/kit/evolve/final-moments.webp';

/**
 * Kit — MHCID Capstone (The Coach’s Kit)
 * Sources: Showcase Presentation (ieyZFG1ZMGlWzPVj7TVmQr),
 * Design-for-Soccer-Recruiting (4kiWtsFEQozwERuqGiuO1p),
 * Soccer Coaching / Design-for-Soccer chats.
 */
export const kitCaseStudy = {
  slug: 'kit',
  editorialMedia: true,
  motion: true,
  chrome: 'meadow',
  paintTheme: 'kit',
  headerLayout: 'stacked',
  headerImage: uiCompare,
  /* Self-playing HTML motion film of the compare board (public/films/kit-compare) */
  headerComponent: 'kit-compare-film',
  headerImageAlt:
    'Kit compare board: three goalkeeper recruits side by side on staff ratings, scholarship ask and GPA, with what each wins on and gives up',
  title: ['Designed Kit, a decision tool that helps coaches', 'narrow 300 players to 5 recruits'],
  titleAccent: 'narrow 300 players to 5 recruits',
  metaLayout: 'facts',
  meta: {
    type: 'Adobe-mentored project',
    role: 'Product designer',
    team: ['5 designers and researchers'],
    timeline: 'Jan–Aug 2026',
  },
  highlightsLayout: 'columns',
  highlightsTitle: 'Highlights',
  /* Highlights removed to match QR Pay (2026-10-26); copy kept below, unused */
  highlights: [],
  _highlightsArchive: [
    {
      num: '01',
      title: 'I led the design',
      body: 'Shaped Kit’s product flows and visual language around how staffs really recruit, from tournament scouting to staff ranking meetings.',
    },
    {
      num: '02',
      title: 'I designed for shared judgment',
      body: 'Rankings encode conviction with spatial gaps, not flat 1, 2, 3 lists, so disagreement becomes visible in the room.',
    },
    {
      num: '03',
      title: 'I kept the coach in charge',
      body: 'Kit assists with prep, capture, and trade-offs. The coaching staff always makes the call.',
    },
  ],
  sections: [
    {
      type: 'content',
      label: 'Why',
      title: 'Our brief: explore how generative AI could help college recruiting',
      paragraphs: [
        'For our capstone, advised by Adobe, a team of five explored how generative AI could streamline college soccer recruiting. Coaches narrow hundreds of prospects to a handful of recruits, and the decision shapes their team for years.',
        'As a product designer, I owned Kit’s information architecture, the coach journey behind it, and the Align phase, where a staff compares recruits and decides.',
      ],
      figure: { component: 'kit-hub' },
    },
    {
      type: 'content',
      label: 'Problem',
      title: 'Coaches narrow 300 players across scattered tools',
      parts: [
        { key: 'funnel', illustration: 'recruit-funnel' },
        { key: 'fragments', heading: 'And the context lives in disconnected tools', illustration: 'recruit-fragments' },
      ],
    },
    {
      type: 'content',
      label: 'Research',
      title: 'We followed coaches onto the field to see where the time went',
      paragraphs: [
        'Our team interviewed 9 subject matter experts and 6 soccer coaches, then followed 3 coaches onto the field. We coded the interviews in Dovetail and affinity-mapped them in FigJam into five insights.',
        'All 6 coaches described working across disconnected tools, and all 6 called time their most constrained resource. One assistant coach said logging every call twice “just seems like a massive waste of your time.” That moved our question from what AI could add to where it could give coaches time back.',
      ],
    },
    {
      type: 'rich',
      label: 'Journey',
      title: 'Kit follows a coach’s day',
      blocks: [
        {
          type: 'paragraph',
          text: 'Coaches don’t open an app to use tools. They orient, capture, ask, and judge. I could have organized Kit by feature, but that would have repeated the scatter coaches already lived with. So I mapped the journey from a roster gap to an offer, then built the information architecture on that sequence.',
        },
        {
          type: 'paragraph',
          text: 'I kept private thinking safe by default: personal space is for drafts, shared space is for deciding. Coaches need room to form a view before the whole staff sees it.',
        },
        { type: 'figure', figure: { component: 'kit-day' } },
        {
          type: 'carousel',
          gallery: {
            variant: 'carousel',
            label: 'phase',
            itemWidth: 1600,
            itemHeight: 900,
            stageAspect: '16 / 9',
            caption: 'The journey in the product: Prepare, Capture, Align.',
            items: [
              { src: prepareInsights, alt: 'Prepare: Kit turns scattered spreadsheets into a shareable ECNL schedule' },
              { src: captureSideline, alt: 'Capture: log key moments from the watch or phone without missing the game' },
              { src: decideShortlist, alt: 'Align: shortlist players and review the roster together' },
            ],
          },
        },
      ],
    },
    {
      type: 'rich',
      label: 'The line',
      title: 'AI gathers information, and coaches decide',
      blocks: [
        {
          type: 'paragraph',
          text: 'Auto-ranking players came up early, but our research pointed the other way. Coaches didn’t trust an AI score, and so much of their decision is still gut feel. So I made the case for one rule: every AI feature gathers, sorts, or summarizes. That gave up the most impressive-looking feature, but a score coaches don’t trust wouldn’t get used.',
        },
        { type: 'figure', figure: { component: 'kit-line' } },
      ],
    },
    {
      type: 'rich',
      label: 'Scenarios',
      title: 'Our advisor pushed us from comparing players to comparing rosters',
      blocks: [
        {
          type: 'paragraph',
          text: 'Our early concepts compared one player against another. Our Adobe advisor pushed us further: the more useful question is what the team looks like with this choice instead of that one. A coach shouldn’t have to clear a roster and rebuild it to test another option.',
        },
        {
          type: 'paragraph',
          text: 'Comparison also happens in conversation, not alone at a desk. So I moved from comparing stats to staging a staff decision around visible trade-offs.',
        },
        { type: 'figure', figure: { component: 'kit-evolve-compare' } },
      ],
    },
    {
      type: 'content',
      label: 'Board',
      title: 'The compare board uses the 1–5 ratings staffs already know',
      paragraphs: [
        'Coaches said a 1–100 AI score was too big a range to mean anything, and preferred the 1–5 ratings their staffs already used. I kept the familiar scale and designed a board the whole staff reads together, with up to three recruits side by side, so the conversation stays on a real choice.',
      ],
      bullets: [
        { lead: 'Must-haves set by the staff.', text: 'Coaches mark what matters for this roster need, and Kit highlights the best player on each.' },
        { lead: 'Wins on, gives up.', text: 'Each column spells out the trade-off, checked against the scholarship budget.' },
        { lead: 'Disagreement stays visible.', text: 'When ratings split, Kit flags it and queues the film for the next staff meeting.' },
      ],
      figure: {
        src: uiCompare,
        alt: 'Kit compare board: three goalkeeper recruits side by side on staff ratings, scholarship ask and GPA, with what each wins on and gives up',
        caption: 'The compare board, with staff must-haves and what each keeper wins on and gives up.',
        role: 'document',
      },
    },
    {
      type: 'rich',
      label: 'Ranking',
      title: 'A ranked list hides how close players are, so I used a line',
      blocks: [
        {
          type: 'paragraph',
          text: 'A flat 1, 2, 3 list hides how sure a staff really is. Coaches asked us to allow closeness, so I replaced the list with a line. Each coach places recruits on it, and the distance between them shows conviction. It’s less tidy than a numbered list, but closer to how a staff talks about recruits.',
        },
        { type: 'figure', figure: { component: 'kit-evolve-rank' } },
        { type: 'figure', figure: { component: 'kit-consensus-film', caption: 'Staff ranking and consensus in Kit’s final design.' } },
      ],
    },
    {
      type: 'rich',
      label: 'Sideline',
      title: 'Coaches can’t stop watching the game to fill in a form',
      blocks: [
        {
          type: 'paragraph',
          text: 'Judgment happens in seconds on the pitch, in the heat, with no laptop. Coaches wanted quick glances, not forms. With each round I took more off the screen, because a missed play costs a coach more than a missing detail. Logging a moment became a single focused action.',
        },
        { type: 'figure', figure: { component: 'kit-evolve-capture' } },
        {
          type: 'figure',
          figure: {
            src: finalMoments,
            alt: 'Kit saved moments screen for Julia Smith with clips and staff notes',
            caption: 'Saved clips and notes the whole staff can return to.',
            role: 'document',
          },
        },
      ],
    },
    {
      type: 'content',
      label: 'Assistant',
      title: 'The assistant sits where coaches already work',
      paragraphs: [
        'Coaches already live in their spreadsheets, and they called a spreadsheet-based concept “very pleasing”, so I put the assistant there: select a player, press a hot key, and see upcoming games and emails. Inside Kit, it surfaces another coach’s notes with their sources. Asked who to pick, it hands the call back to the staff, in line with the rule we set.',
      ],
      figuresFull: true,
      figures: [
        { component: 'kit-lookup-film', caption: 'A hot key from the coach’s spreadsheet.' },
        { component: 'kit-sidebar-film', caption: 'Quick context at the side of Kit.' },
      ],
    },
    {
      type: 'rich',
      label: 'Workshop',
      title: 'Our prototypes all looked different, so I ran a workshop to align them',
      blocks: [
        {
          type: 'paragraph',
          text: 'We had each prototyped tools that looked totally different, and Kit needed to read as one product. I ran a workshop that moved from brand words, to a few key screens designed in different ways, down to one direction. We chose dark because data-heavy screens need bright signals to stand out.',
        },
        {
          type: 'figure',
          figure: {
            src: directions,
            alt: 'The Kit roster explored as a light depth grid, a light pitch, a dark pitch, and a dark 3D pitch',
            caption: 'Light and dark directions for the same roster screen.',
            role: 'document',
          },
        },
      ],
    },
    {
      type: 'rich',
      label: 'Testing',
      title: 'We reviewed 27 features with 3 D1 coaches, and they told us to simplify',
      blocks: [
        {
          type: 'paragraph',
          text: 'Of the 27 features, 6 were strong positives, 1 was a critical gap, and 1 we chose to deprioritize. Coaches without data-science backgrounds got overwhelmed, and one put it plainly: “Simplicity is genius.” That changed what we worked on next.',
        },
        {
          type: 'goal-map',
          heads: ['What coaches told us', 'What it meant for the design'],
          rows: [
            { goal: 'Less data-heavy, please', change: 'One job per screen, with progressive disclosure' },
            { goal: 'Weight traits by position and game model', change: 'Make weighting the core of ranking and comparison' },
            { goal: 'How does data get in?', change: 'Prioritize intake before advanced analytics' },
            { goal: 'Depth charts are how we think', change: 'Lead with the depth chart, and pair charts with it' },
          ],
        },
      ],
    },
    {
      type: 'outcome-split',
      label: 'Outcome',
      title: 'Coaches responded well to the prototype',
      paragraphs: [
        'For the MHCI+D Capstone Showcase, I owned the narrative and directed the live demo for industry professionals and our Adobe advisors. Kit is a prototype, so the measure here is coach feedback, not usage, and that feedback told us we were solving the right problem.',
      ],
      figure: {
        src: feedback,
        alt: 'Feedback from a D1 women’s assistant coach: “This is just great. I would never have thought of this… so impressed with what you have already created!”',
        caption: 'Feedback from a D1 women’s assistant coach.',
        role: 'document',
      },
    },
    {
      type: 'content',
      label: 'With AI',
      title: 'I built a live prototype with Claude and Cursor',
      paragraphs: [
        'I built the compare board, consensus ranking, and AI integration as a live prototype using Claude and Cursor. I also used AI to brainstorm, edit in Figma, and create motion design, choosing it step by step based on whether it improved the work or sped it up.',
        'I briefed Claude with my own rules, like “Each screen should focus on one task”, then corrected what came back. A year slider in the sitemap became simple chips for the current year plus three. A separate shared workspace became one account toggle between personal and shared spaces.',
      ],
      figure: {
        src: finalRoster,
        alt: 'Kit roster screen in the final dark design',
        caption: 'The final roster view.',
        role: 'document',
      },
    },
    {
      type: 'reflection',
      label: 'Takeaways',
      title: 'What I learned building Kit',
      takeaways: [
        {
          title: 'Aligning the team matters as much as aligning the user',
          body: 'The workshop taught me how to guide a room of opinionated designers toward one system without losing momentum.',
        },
        {
          title: 'Design for a group decision',
          body: 'The compare board taught me how UI can mediate a shared, high-stakes call.',
        },
        {
          title: 'Know what AI should not do',
          body: 'The clearest design decision was the line I drew around the coach’s judgment.',
        },
      ],
    },
  ],
};
